'use client'

import { useAudioPlayer } from '@/components/client/audio/use-audio-player'
import { useTranslation } from '@/hooks/client/use-translation'
import SvgSpinner from '@/icons/regular/SpinnerThird'
import SvgPause from '@/icons/solid/Pause'
import SvgPlay from '@/icons/solid/Play'
import { tw } from '@/lib/utils'

interface RowListenButtonProps {
  url: string
  name: string
}

export const RowListenButton = ({ url, name }: RowListenButtonProps) => {
  const { t } = useTranslation()
  const { audioRef, status, toggle, isMounted, audioProps } = useAudioPlayer({ url, group: 'directory' })
  if (!isMounted) {
    return null
  }
  const action = status.isLoading
    ? t('audio.loading', 'Loading audio')
    : status.isPlaying
      ? t('audio.pause', 'Pause audio')
      : t('audio.play', 'Play audio')
  const Icon = status.isLoading ? SvgSpinner : status.isPlaying ? SvgPause : SvgPlay

  return (
    <>
      <button
        type="button"
        data-tracking="ignore"
        aria-label={`${action} – ${name}`}
        aria-pressed={status.isPlaying}
        aria-busy={status.isLoading}
        disabled={status.isLoading}
        onClick={(event) => {
          event.stopPropagation()
          toggle()
        }}
        className={tw(
          'focus-visible:outline-lapis inline-flex h-[44px] w-[44px] shrink-0 cursor-pointer items-center justify-center rounded-xl border text-[14px] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-wait sm:w-auto sm:gap-2 sm:px-3',
          status.isPlaying
            ? 'border-navy bg-navy hover:bg-navy/90 text-white'
            : 'border-navy/25 text-navy hover:border-navy bg-white'
        )}
      >
        <Icon
          aria-hidden
          className={tw(
            'h-[18px] w-[18px] shrink-0 fill-current',
            status.isLoading && 'animate-spin motion-reduce:animate-none'
          )}
        />
        <span className="hidden sm:inline">
          {status.isPlaying ? t('directory.row.pause', 'Pause') : t('directory.row.listen', 'Listen')}
        </span>
      </button>
      <audio ref={audioRef} {...audioProps} aria-hidden tabIndex={-1} className="hidden" />
    </>
  )
}
